import { useFeatureGate } from '@statsig/react-bindings';

import { useBranchBannerPage, useBranchJourney } from '@growtherapy/analytics/client';

import { telehealthBranchSmartBannersGate } from '../statsig/gates';
import { useGetPublicPatient } from '../useGetPublicPatientByShortId';
import { VisitorState } from './state';

/**
 * Initializes the Branch.io journey (smart banner) for the visitor flow.
 *
 * The banner is only triggered when all three conditions hold:
 * 1. `telehealthBranchSmartBanners` Statsig gate is enabled.
 * 2. The patient's `publicPatient.showSmartBanner` field is true — i.e. the
 *    backend has marked this client as eligible.
 * 3. The current `visitorState` corresponds to a page where the banner should
 *    render (see `useBranchBannerPage`).
 *
 * `useBranchJourney` reacts to changes in any of these inputs, so the banner
 * is shown/hidden as the visitor moves between intro / waiting / meeting
 * states.
 *
 *
 * @param patientShortId - Short ID from the URL; used to fetch the public
 *   patient record that carries the eligibility flag. May be undefined while
 *   params are still resolving.
 * @param visitorState - Current visitor state, used both to gate eligible
 *   pages and to drive Branch journey transitions.
 */
export function useBranchSmartBanner(patientShortId: string | undefined, visitorState: VisitorState) {
  const { data: publicPatientData } = useGetPublicPatient(patientShortId);
  const { value: smartBannerFFEnabled } = useFeatureGate(telehealthBranchSmartBannersGate);
  const clientEligibleForSmartBanner = publicPatientData?.publicPatient.showSmartBanner ?? false;

  // Normally, we would use the pathname to determine if the current page is a Branch page.
  // However, in the Telehealth app, the pathname doesn't change when the visitor state changes.
  // So, we use the visitorState directly to determine if the current page is a Branch page.
  // Please, add visitor states where the banner should be shown to this dynamic config:
  // https://console.statsig.com/ehmOdP8c3qeFibZ6BvwUV/dynamic_configs/branch_smart_banner_routes
  const isBranchPage = useBranchBannerPage(visitorState);

  useBranchJourney(smartBannerFFEnabled && clientEligibleForSmartBanner && isBranchPage, visitorState);
}
